import React, { useState, useRef, useEffect } from "react"
import Slider from "react-slick"
import gsap from "gsap"
import { useGSAP } from "@gsap/react"
import { debounce } from "lodash"

import { TextElementNova } from "@nutrafol/nutrafol-ui-kit/dist/TextElementNova"

import {
  SlideWrapper,
  SlideTrack,
  Slide,
  Divider,
  CauseText,
  Dots,
  Dot,
} from "./quizbanner.styled"

const HEIGHT = 260
const waves = [1, 20, 40, 60] // y values for the waves

function getFactor(value, values) {
  const max = Math.max(...values.map((v) => v.value))

  return value / max
}

const getShape = (y, factor) => {
  const computedY = HEIGHT + y - Math.abs(factor) * (HEIGHT - 1)
  return `M0 ${HEIGHT}C106 ${HEIGHT} 89 ${computedY} 150 ${computedY}C211 ${computedY} 193 ${HEIGHT} 300 ${HEIGHT}`
}

const Wave = ({ y, index, factor, isActive }) => {
  const waveRef = useRef(null)

  useGSAP(() => {
    if (waveRef.current && isActive) {
      gsap.fromTo(
        waveRef.current,
        {
          attr: { d: getShape(y + 10, factor) },
        },
        {
          duration: 1,
          attr: { d: getShape(y, factor) },
          ease: "circ.out",
          delay: 1,
        }
      )
    }
    if (waveRef.current && !isActive) {
      gsap.fromTo(
        waveRef.current,
        {
          attr: { d: getShape(y, factor) },
        },
        {
          duration: 1,
          attr: { d: getShape(y + 10, factor) },
          ease: "circ.out",
          delay: 1,
        }
      )
    }
  }, [isActive])

  return (
    <path
      shapeRendering="geometricPrecision"
      d={getShape(y + 10, factor)}
      strokeLinejoin="round"
      ref={waveRef}
      stroke="#fff"
      strokeWidth="1"
      opacity={1 - index * 0.25}
    />
  )
}

export default function WavesSlider({ causes }) {
  const wrapperRef = useRef(null)
  const sliderRef = useRef(null)
  const causeTextRef = useRef(null)
  const dotRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)

  useGSAP(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapperRef.current,
        start: "top center",
        end: "center center",
        toggleActions: "play none none reset",
        pinnedContainer: "#growth-pin-wrapper",
        invalidateOnRefresh: true,
      },
    })

    tl.fromTo(wrapperRef.current, { opacity: 0 }, { opacity: 1, duration: 1 })
  })

  useGSAP(() => {
    const tl = gsap.timeline({
      repeat: -1,
      repeatDelay: 3,
      scrollTrigger: {
        trigger: wrapperRef.current,
        start: "30% center",
        toggleActions: "play none none reset",
        pinnedContainer: "#growth-pin-wrapper",
        invalidateOnRefresh: true,
      },
    })

    tl.fromTo(
      causeTextRef.current,
      {
        opacity: 1,
      },
      {
        opacity: 0,
        duration: 0.3,
      }
    )
      .add(() => {
        sliderRef.current.slickNext()
      }, 0.3)
      .fromTo(
        causeTextRef.current,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.3,
          delay: 0.5,
        },
        1
      )
  })

  const setDotAndTextPos = () => {
    if (!causeTextRef.current || !dotRef.current) return
    const sliderHeight = sliderRef.current.innerSlider.list.offsetHeight
    const yBottomPos =
      sliderHeight * getFactor(causes[activeIndex].value, causes) -
      causes[activeIndex].value

    gsap.set(causeTextRef.current, {
      y: -Math.abs(yBottomPos) - 20,
    })

    gsap.to(dotRef.current, {
      ease: "circ.out",
      duration: 1,
      y: -Math.abs(yBottomPos) - dotRef.current.offsetHeight / 2,
    })
  }

  useGSAP(
    () => {
      setDotAndTextPos()
    },
    { dependencies: [activeIndex, causes] }
  )

  useEffect(() => {
    if (typeof window === "undefined") return
    const debouncedSetDotAndTextPos = debounce(() => setDotAndTextPos(), 500)
    window.addEventListener("resize", debouncedSetDotAndTextPos)
    return () => window.removeEventListener("resize", debouncedSetDotAndTextPos)
  }, [activeIndex])

  const settings = {
    dots: false,
    infinite: true,
    slidesToScroll: 1,
    arrows: false,
    speed: 1000,
    slidesToShow: 1,
    centerMode: true,
    variableWidth: true,
    autoplay: false,
    touchMove: false,
    beforeChange: (_, index) => {
      setActiveIndex(index)
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.7,
        },
      },
    ],
  }

  return (
    <SlideWrapper ref={wrapperRef}>
      <Divider />
      <SlideTrack>
        <CauseText ref={causeTextRef}>
          <TextElementNova
            text={causes[activeIndex].name}
            className="Caption-10-Dia-Uppercase text-nova-base-white"
          />
        </CauseText>
        <Slider {...settings} ref={sliderRef}>
          {causes.map((cause, i) => {
            const factor = getFactor(cause.value, causes)
            return (
              <Slide key={i} width={[1 / 3, 1 / 3, 1 / 3, 1 / 2]}>
                <svg
                  width="100%"
                  viewBox="0 0 300 261"
                  fill="none"
                  style={{ transform: "translate3d(0px, 0px, 0px)" }}
                >
                  {waves.map((y, ii) => (
                    <Wave
                      key={ii}
                      isActive={i === activeIndex}
                      y={y}
                      index={ii}
                      factor={factor}
                    />
                  ))}
                </svg>
              </Slide>
            )
          })}
        </Slider>
        <Dots>
          <Dot ref={dotRef} />
          <Dot className="static" />
        </Dots>
      </SlideTrack>
    </SlideWrapper>
  )
}
